import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Avatar,
  List,
  ListItem,
  ListItemSecondaryAction,
  IconButton,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Event as EventIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import axios from 'axios';
import useTranslation from '../hooks/useTranslation';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { useHistory, Link as RouterLink } from 'react-router-dom';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

const ITEMS_PER_PAGE = 6;

const OperatorDashboard = () => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [tours, setTours] = useState([]);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editedProfile, setEditedProfile] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [page, setPage] = useState(1);
  const [tourDates, setTourDates] = useState({});
  const [tourToDelete, setTourToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [previewImage, setPreviewImage] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [createTourDialog, setCreateTourDialog] = useState(false);
  const history = useHistory();

  useEffect(() => {
    fetchProfile();
    fetchTours();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get('/operators/profile');
      setProfile(response.data);
      setEditedProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error(t('pages.tours.operator.dashboard.notifications.loadError.profile'));
    }
  };

  const fetchTours = async () => {
    try {
      const response = await axios.get('/operators/tours');
      setTours(response.data);
      
      // Fetch dates for each tour
      const datesPromises = response.data.map(tour => 
        axios.get(`/operators/tours/${tour.id}/dates`)
          .then(res => ({ tourId: tour.id, dates: res.data }))
      );
      
      const datesResults = await Promise.all(datesPromises);
      const datesMap = {};
      datesResults.forEach(({ tourId, dates }) => {
        datesMap[tourId] = dates;
      });
      setTourDates(datesMap);
    } catch (error) {
      console.error('Error fetching tours:', error);
      toast.error(t('pages.tours.operator.dashboard.notifications.loadError.tours'));
    }
  };

  const handleImageChange = async (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);

      // Start upload
      setIsUploadingImage(true);
      setImageUploadProgress(0);
      
      try {
        const formData = new FormData();
        formData.append('image', file);
        
        const response = await axios.post('/operators/profile/image', formData, {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setImageUploadProgress(progress);
          }
        });

        setEditedProfile(prev => ({
          ...prev,
          profileImage: response.data.profileImage
        }));
        toast.success(t('pages.tours.operator.dashboard.notifications.profileUpdateSuccess'));
      } catch (error) {
        console.error('Error uploading image:', error);
        toast.error(t('pages.tours.operator.dashboard.notifications.profileUpdateError'));
        setPreviewImage(null);
      } finally {
        setIsUploadingImage(false);
        setImageUploadProgress(0);
      }
    }
  };

  const handleProfileUpdate = async () => {
    try {
      setIsLoading(true);
      await axios.put('/operators/profile', editedProfile);
      toast.success(t('pages.tours.operator.dashboard.notifications.profileUpdateSuccess'));
      setIsEditingProfile(false);
      fetchProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('pages.tours.operator.dashboard.notifications.profileUpdateError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTourVisibility = async (tourId, isPublic) => {
    try {
      await axios.put(`/operators/tours/${tourId}`, { isPublic });
      fetchTours();
      toast.success(t('pages.tours.operator.dashboard.notifications.tourVisibilitySuccess'));
    } catch (error) {
      console.error('Error updating tour visibility:', error);
      toast.error(t('pages.tours.operator.dashboard.notifications.tourVisibilityError'));
    }
  };

  const handleDeleteTour = async (tourId) => {
    try {
      await axios.delete(`/operators/tours/${tourId}`);
      fetchTours();
      toast.success(t('pages.tours.operator.dashboard.notifications.tourDeleteSuccess'));
      setTourToDelete(null);
    } catch (error) {
      console.error('Error deleting tour:', error);
      toast.error(t('pages.tours.operator.dashboard.notifications.tourDeleteError'));
    }
  };

  const handleCreateTour = () => {
    history.push('/operator/tours/new');
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const getDateDisplay = (tour) => {
    const dates = tourDates[tour.id] || [];
    if (!dates.length) return null;

    const sortedDates = dates.map(d => new Date(d.date)).sort((a, b) => a - b);
    const firstDate = format(sortedDates[0], 'MMM d, yyyy', { locale: t('common.locale') === 'es' ? es : enUS });
    
    if (dates.length === 1) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventIcon fontSize="small" sx={{ color: '#666' }} />
          <Typography variant="body2" color="text.secondary">
            {firstDate}
          </Typography>
        </Box>
      );
    }

    const lastDate = format(sortedDates[sortedDates.length - 1], 'MMM d, yyyy', { locale: t('common.locale') === 'es' ? es : enUS });
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EventIcon fontSize="small" sx={{ color: '#666' }} />
        <Typography variant="body2" color="text.secondary">
          {firstDate} - {lastDate}
        </Typography>
        <Chip 
          size="small" 
          label={`${dates.length} ${t('pages.tours.operator.dashboard.tourDates.dates')}`} 
          sx={{ 
            ml: 1,
            bgcolor: 'transparent',
            border: '1px solid #666',
            color: '#666',
            height: '20px'
          }}
        />
      </Box>
    );
  };

  const paginatedTours = tours.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7f9' }}>
      <Navbar hideFilters sx={{ bgcolor: '#f5f7f9' }} />
      <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
        <Grid container spacing={4}>
          {/* Profile Section */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, bgcolor: '#fff', height: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Avatar
                  src={previewImage || profile?.profileImage}
                  sx={{ 
                    width: 100, 
                    height: 100,
                    mb: 2,
                    position: 'relative' 
                  }}
                >
                  {isUploadingImage && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        borderRadius: '50%'
                      }}
                    >
                      {imageUploadProgress}%
                    </Box>
                  )}
                </Avatar>
                {isEditingProfile && (
                  <Button 
                    component="label"
                    disabled={isUploadingImage}
                    variant="outlined"
                    size="small"
                  >
                    {isUploadingImage 
                      ? t('common.loading')
                      : t('pages.tours.operator.dashboard.uploadPhoto')
                    }
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </Button>
                )}
              </Box>

              {isEditingProfile ? (
                <Box component="form">
                  <TextField
                    fullWidth
                    label={t('pages.tours.operator.dashboard.businessName')}
                    value={editedProfile.name || ''}
                    onChange={(e) => setEditedProfile({ ...editedProfile, name: e.target.value })}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label={t('pages.tours.operator.dashboard.phone')}
                    value={editedProfile.phone || ''}
                    onChange={(e) => setEditedProfile({ ...editedProfile, phone: e.target.value })}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label={t('pages.tours.operator.dashboard.description')}
                    value={editedProfile.description || ''}
                    onChange={(e) => setEditedProfile({ ...editedProfile, description: e.target.value })}
                    margin="normal"
                  />
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleProfileUpdate}
                      disabled={isLoading}
                      sx={{ bgcolor: '#316C6E', '&:hover': { bgcolor: '#254F51' } }}
                    >
                      {isLoading ? 'Saving...' : t('pages.tours.operator.dashboard.saveChanges')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setIsEditingProfile(false);
                        setEditedProfile(profile);
                      }}
                    >
                      {t('pages.tours.operator.dashboard.cancel')}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <TextField
                    fullWidth
                    label={t('pages.tours.operator.dashboard.businessName')}
                    value={profile?.name || ''}
                    margin="normal"
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                  <TextField
                    fullWidth
                    label={t('pages.tours.operator.dashboard.phone')}
                    value={profile?.phone || ''}
                    margin="normal"
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label={t('pages.tours.operator.dashboard.description')}
                    value={profile?.description || ''}
                    margin="normal"
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditingProfile(true)}
                    >
                      {t('pages.tours.operator.dashboard.editProfile')}
                    </Button>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Tours Section */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5">{t('pages.tours.operator.dashboard.myTours')}</Typography>
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<ReceiptIcon />}
                  onClick={() => history.push('/operator-payment-history')}
                  sx={{ mr: 2 }}
                >
                  {t('pages.tours.operator.dashboard.paymentHistory')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateTour}
                  sx={{ bgcolor: '#316C6E', '&:hover': { bgcolor: '#254F51' } }}
                >
                  {t('pages.tours.operator.dashboard.createNewTour')}
                </Button>
              </Box>
            </Box>

            <Paper sx={{ bgcolor: '#fff' }}>
              <List>
                {paginatedTours.map((tour, index) => (
                  <React.Fragment key={tour.id}>
                    {index > 0 && <Divider />}
                    <ListItem
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                        cursor: 'pointer',
                        pr: '280px' // Reserve space for buttons
                      }}
                      onClick={() => history.push(`/operator/tours/${tour.id}/manage`)}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography 
                            variant="h6" 
                            component="div"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              width: '100%'
                            }}
                          >
                            {tour.name}
                          </Typography>
                        </Box>
                        {getDateDisplay(tour)}
                      </Box>
                      <ListItemSecondaryAction>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTourVisibility(tour.id, !tour.isPublic);
                          }}
                          startIcon={tour.isPublic ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          sx={{
                            mr: 1,
                            borderColor: tour.isPublic ? '#2e7d32' : '#666',
                            color: tour.isPublic ? '#2e7d32' : '#666',
                            '&:hover': {
                              borderColor: tour.isPublic ? '#1b5e20' : '#444',
                              bgcolor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          {tour.isPublic ? t('pages.tours.operator.dashboard.tourVisibility.public') : t('pages.tours.operator.dashboard.tourVisibility.private')}
                        </Button>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setTourToDelete(tour.id);
                          }}
                          sx={{ color: '#d32f2f' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
              {tours.length > ITEMS_PER_PAGE && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <Pagination 
                    count={Math.ceil(tours.length / ITEMS_PER_PAGE)} 
                    page={page} 
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Delete Confirmation Modal */}
      <Dialog 
        open={!!tourToDelete} 
        onClose={() => setTourToDelete(null)}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ color: '#d32f2f' }}>{t('pages.tours.operator.dashboard.deleteTour.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('pages.tours.operator.dashboard.deleteTour.message')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setTourToDelete(null)}
            sx={{ color: '#666' }}
          >
            {t('pages.tours.operator.dashboard.deleteTour.cancel')}
          </Button>
          <Button 
            onClick={() => handleDeleteTour(tourToDelete)}
            variant="contained"
            sx={{ 
              bgcolor: '#d32f2f',
              '&:hover': { bgcolor: '#b22a2a' }
            }}
          >
            {t('pages.tours.operator.dashboard.deleteTour.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperatorDashboard; 